import type { Camera, Object3D } from "three";
import { Vector3 } from "three";

import type { FigureData } from "../data/octagonOnCube";

export interface GridDotsPaneConfig {
  cssX: number; // ペイン左上の CSS x (top-left 基準, px)
  cssY: number; // ペイン左上の CSS y
  w: number;
  h: number;
  show: boolean;
  color: string;
}

/**
 * grid モード専用のドット描画。WebGL の Points は billboard が hull や視点角
 * 次第でクリップされてしまうため、normal モードの CSS2D ドットと同じく
 * DOM div を CSS 配置する方式に揃える。ただし CSS2DRenderer のように
 * カメラ投影に丸任せできない (4 ペインそれぞれに別位置・別スケールで描く
 * 必要があるため)、ここで NDC 投影を手動で行ってペインごとに位置する。
 *
 * 24 頂点 × 4 ペイン = 96 個の div を保持し、毎フレーム可視頂点のみ表示。
 * z-buffer / depthTest / polygonOffset などの 3D 描画パイプラインから完全に
 * 独立しているので、角度によらず常に完全な円。
 */
export class GridDots {
  private readonly data: FigureData;
  private readonly panes: HTMLDivElement[][];
  private readonly tmp: Vector3;

  constructor(data: FigureData, container: HTMLElement) {
    this.data = data;
    this.tmp = new Vector3();
    this.panes = [];
    for (let p = 0; p < 4; p++) {
      const dots: HTMLDivElement[] = [];
      for (let v = 0; v < data.vertices.length; v++) {
        const div = document.createElement("div");
        div.className = "vertex-dot grid-dot";
        div.style.position = "absolute";
        div.style.left = "0";
        div.style.top = "0";
        div.style.display = "none";
        container.appendChild(div);
        dots.push(div);
      }
      this.panes.push(dots);
    }
  }

  setSize(sizePx: number): void {
    for (const pane of this.panes) {
      for (const el of pane) {
        el.style.width = `${sizePx}px`;
        el.style.height = `${sizePx}px`;
      }
    }
  }

  hideAll(): void {
    for (const pane of this.panes) {
      for (const el of pane) {
        el.style.display = "none";
      }
    }
  }

  /**
   * 4 ペイン分の位置・表示・色を一括更新する。
   * opacities は normal モードと同じ「3 面 OR 法線判定」結果を fade lerp した
   * per-vertex opacity (0..1)。境界角での 0/1 切替を softer にし、視点回転中の
   * 点 pop-in/pop-out flicker を吸収する。閾値以下は display:none。
   */
  update(
    root: Object3D,
    camera: Camera,
    opacities: number[],
    paneConfigs: GridDotsPaneConfig[],
  ): void {
    for (let p = 0; p < this.panes.length; p++) {
      const cfg = paneConfigs[p];
      const dots = this.panes[p];

      if (!cfg.show) {
        for (const el of dots) el.style.display = "none";
        continue;
      }

      for (let v = 0; v < this.data.vertices.length; v++) {
        const el = dots[v];
        const op = opacities[v];
        if (op < 0.001) {
          el.style.display = "none";
          continue;
        }

        // 頂点を world → NDC (-1..1) に投影
        const [x, y, z] = this.data.vertices[v];
        this.tmp.set(x, y, z).applyMatrix4(root.matrixWorld).project(camera);

        // NDC をペインの CSS pixel coords にマップ。NDC y は上が +1 なので
        // CSS y (下が +) に反転。
        const px = cfg.cssX + ((this.tmp.x + 1) / 2) * cfg.w;
        const py = cfg.cssY + ((1 - this.tmp.y) / 2) * cfg.h;

        el.style.display = "block";
        el.style.opacity = String(op);
        el.style.backgroundColor = cfg.color;
        el.style.transform = `translate(-50%, -50%) translate(${px}px, ${py}px)`;
      }
    }
  }
}
